// Settings › Partner rules (Phase 4b plan §2.1–§2.3): your rules, rules other retailers set, and the
// two confirmations (turning a rule off, changing "shoppers we can't locate").
import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  MenuItem,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Link as RouterLink, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { dashboardAPI } from '../../../services/dashboard-api';
import { track } from '../../../utils/analytics';
import RuleCard from './RuleCard';
import { unknownDialog } from './copy';

const Empty = ({ children }) => (
  <Card>
    <CardContent sx={{ px: { xs: 2, sm: 3 }, '&:last-child': { pb: 2 } }}>
      <Typography color="text.secondary">{children}</Typography>
    </CardContent>
  </Card>
);

export default function PartnerRulesTab() {
  const [searchParams, setSearchParams] = useSearchParams();
  const retailerParam = searchParams.get('retailer') || undefined;
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(location.state?.notice || null);
  const [dialog, setDialog] = useState(null); // {kind: 'off' | 'unknown', card, exclude}
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    setError(null);
    return dashboardAPI
      .getPartnerRules(retailerParam)
      .then(setData)
      .catch((e) => setError(e.message));
  }, [retailerParam]);

  useEffect(() => {
    load();
  }, [load]);

  // The builder hands its notice over in the history state; clear it so a reload does not show it again.
  useEffect(() => {
    if (location.state?.notice) navigate({ pathname: location.pathname, search: location.search }, { replace: true, state: null });
  }, [location, navigate]);

  const write = async (call, message) => {
    setBusy(true);
    setNotice(null);
    try {
      await call();
      setNotice(message);
      setDialog(null);
      await load();
    } catch (e) {
      setDialog(null);
      setError(e.message);
    }
    setBusy(false);
  };

  const onToggle = (card) => {
    if (card.on) {
      setDialog({ kind: 'off', card });
    } else {
      track('Partner Rule Turned On Again');
      write(() => dashboardAPI.turnPartnerRule(card.id, true, retailerParam), 'The rule is on again.');
    }
  };

  const confirmOff = () => {
    track('Partner Rule Turned Off');
    write(() => dashboardAPI.turnPartnerRule(dialog.card.id, false, retailerParam), 'The rule is off.');
  };

  const confirmUnknown = () => {
    track('Partner Rule Unknown Shoppers Changed', { exclude: dialog.exclude });
    write(
      () => dashboardAPI.changePartnerRule(dialog.card.id, { unknown_excluded: dialog.exclude }, retailerParam),
      'Saved. The change works at once, on your site and on partner sites.'
    );
  };

  const goTo = (path) => navigate({ pathname: path, search: location.search });

  if (!data && !error) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  const unknown = dialog?.kind === 'unknown' ? unknownDialog(dialog.card, dialog.exclude) : null;

  return (
    <Stack spacing={2.5}>
      {data?.group?.length > 1 && (
        <TextField
          select
          label="Shop"
          value={data.retailer_id}
          onChange={(e) => setSearchParams(e.target.value ? { retailer: e.target.value } : {})}
          sx={{ maxWidth: 360 }}
        >
          {data.group.map((g) => (
            <MenuItem key={g.retailer_id} value={g.retailer_id}>
              {g.name}
            </MenuItem>
          ))}
        </TextField>
      )}

      {notice && (
        <Alert severity="success" onClose={() => setNotice(null)}>
          {notice}
        </Alert>
      )}
      {error && <Alert severity="error">{error}</Alert>}
      {data && !data.can_edit && (
        <Alert severity="info">
          Only admin users can change partner rules. Ask an admin in your team, or{' '}
          <Link href="mailto:info@handoffleads.com">contact HandOff</Link>.
        </Alert>
      )}

      <Typography sx={{ maxWidth: 760 }}>
        You can set up partner rules to manage which partners appear on your site. Rules always work in both
        directions.
      </Typography>

      {data && (
        <>
          <Stack component="section" spacing={1.5}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
              <Typography variant="h6" component="h2">
                Your rules
              </Typography>
              {data.can_edit && (
                <Button variant="outlined" onClick={() => goTo('/settings/partner-rules/new')} sx={{ minHeight: 44 }}>
                  Add a rule
                </Button>
              )}
            </Stack>
            {data.own.length ? (
              data.own.map((card) => (
                <RuleCard
                  key={card.id}
                  card={card}
                  own
                  canEdit={data.can_edit}
                  busy={busy}
                  onToggle={onToggle}
                  onUnknown={(c, exclude) => setDialog({ kind: 'unknown', card: c, exclude })}
                  onChange={(c) => goTo(`/settings/partner-rules/${c.id}/change`)}
                />
              ))
            ) : (
              <Empty>You have no rules. You are shown on every partner's site, and every partner is shown on yours.</Empty>
            )}
          </Stack>

          <Stack component="section" spacing={1.5}>
            <Typography variant="h6" component="h2">
              Rules other retailers set
            </Typography>
            {data.others.length ? (
              data.others.map((card) => <RuleCard key={card.id} card={card} own={false} />)
            ) : (
              <Empty>No other retailer has a rule that affects your shop.</Empty>
            )}
          </Stack>

          <Typography color="text.secondary">
            See what these rules do in{' '}
            <Link component={RouterLink} to="/?tab=clicks">
              Dashboard › Clicks &amp; orders
            </Link>
            .
          </Typography>
        </>
      )}

      <Dialog open={dialog?.kind === 'off'} onClose={() => setDialog(null)} fullScreen={fullScreen} maxWidth="xs">
        <DialogTitle>Turn off this rule?</DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            Partners are shown to every shopper again, on your site and on theirs. This starts at once.
          </Typography>
          <Typography color="text.secondary">You can turn the rule on again at any time.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog(null)}>Cancel</Button>
          <Button variant="contained" onClick={confirmOff} disabled={busy}>
            Turn off
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!unknown} onClose={() => setDialog(null)} fullScreen={fullScreen} maxWidth="sm">
        {unknown && (
          <>
            <DialogTitle>{unknown.title}</DialogTitle>
            <DialogContent>
              {unknown.lines.map((line, i) => (
                <Typography key={line} color={i === 2 ? 'text.secondary' : 'text.primary'} gutterBottom>
                  {line}
                </Typography>
              ))}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDialog(null)}>Cancel</Button>
              <Button variant="contained" onClick={confirmUnknown} disabled={busy}>
                Save
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Stack>
  );
}
